import { flushSync } from 'react-dom'
import { preloadablePage } from '@/lib/lazyPage'
import { sdk } from '@/lib/sdk'
import { loadSubscriptionGroups } from './subscriptionGroupsCache'

/**
 * The plan's checkout (`/pricing/{plan}/{option}`), as `App` routes it. Kept
 * here so the places that open it (the plans page, the Store, a padlocked
 * space's offer) can fetch its code ahead.
 */
export const CheckoutSubscriptionsPage = preloadablePage(() => import('@/pages/CheckoutSubscriptions'), 'CheckoutSubscriptionsPage')

/** The names the plan's name and price fly under, from the card or row to the checkout's header. */
export const PLAN_NAME_TRANSITION = 'plan-checkout-name'
export const PLAN_PRICE_TRANSITION = 'plan-checkout-price'

/**
 * On hover or focus of something that opens a plan's checkout: its code and
 * the plan list it opens on. Errors are left for the navigation itself.
 */
export const prefetchPlanCheckout = (communityId: string | null | undefined = sdk.getCommunityId()) => {
  void CheckoutSubscriptionsPage.preload().catch(() => undefined)
  if (communityId) void loadSubscriptionGroups(communityId).catch(() => undefined)
}

/** The plan card a click or a key came from (the Store's cards), for the flight to the checkout. */
export const planCardOf = (element: Element | null | undefined): Element | null =>
  element?.closest('[data-testid=storefront-plan-card]') ?? null

type ViewTransitionDocument = Document & { startViewTransition?: (update: () => void) => { finished: Promise<void> } }

const reducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

const settle = <T,>(promise: Promise<T>) => promise.then(() => true, () => false)

/**
 * Opens a plan's checkout from a card or a row (David, 2026-10-30: "deixar
 * bonito quando clica"). It waits a moment for the page's code and the plan
 * list, so the checkout renders whole on its first frame; then, where the
 * browser has View Transitions and motion is welcome, the plan's name and
 * price fly from `source` (elements marked `data-plan-transition="name"` and
 * `"price"`) to the checkout's header. Otherwise, or when the code is still on
 * its way, it simply navigates.
 */
export const openPlanCheckout = async ({
  navigate,
  path,
  communityId = sdk.getCommunityId(),
  source
}: {
  navigate: (path: string) => void
  path: string
  communityId?: string | null
  source?: Element | null
}) => {
  const code = settle(CheckoutSubscriptionsPage.preload())
  const data = communityId ? settle(loadSubscriptionGroups(communityId)) : Promise.resolve(true)
  const timeout = new Promise<false>((resolve) => window.setTimeout(() => resolve(false), 350))
  const ready = await Promise.race([Promise.all([code, data]).then(([a, b]) => a && b), timeout])

  const doc = document as ViewTransitionDocument
  if (!ready || !doc.startViewTransition || reducedMotion()) {
    navigate(path)
    return
  }
  const tagged: HTMLElement[] = []
  const tag = (role: string, name: string) => {
    const el = source?.querySelector<HTMLElement>(`[data-plan-transition="${role}"]`)
    if (!el) return
    el.style.setProperty('view-transition-name', name)
    tagged.push(el)
  }
  tag('name', PLAN_NAME_TRANSITION)
  tag('price', PLAN_PRICE_TRANSITION)
  try {
    const transition = doc.startViewTransition(() => {
      /* The old elements leave the snapshot: the new page carries the names now. */
      tagged.forEach((el) => el.style.removeProperty('view-transition-name'))
      flushSync(() => navigate(path))
    })
    void transition.finished.catch(() => undefined)
  } catch {
    tagged.forEach((el) => el.style.removeProperty('view-transition-name'))
    navigate(path)
  }
}
